import { useCallback, useEffect } from 'react';
import { content_v1_PresetDataSourceList } from '@antimatterhq/asl-api-ts-v1';
import { Button } from '@antimatterhq/ui/components/button';
import { Icon } from '@antimatterhq/ui/components/icon';
import Spacer from '@antimatterhq/ui/components/spacer';
import { Text } from '@antimatterhq/ui/components/typography';
import { useGenericFuzzyFiltering } from '@antimatterhq/ui/hooks/fuzzyFiltering/useGenericFuzzyFiltering';
import { FlagSet } from '@antimatterhq/ui/utils/flags';
import { useAtom, useAtomValue } from 'jotai';
import { useAtomCallback } from 'jotai/utils';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { AxiosError } from 'axios';
import Tooltip from '@antimatterhq/ui/components/tooltip';
import {
  dataSourceFilterAtom,
  dataSourceViewModeAtom,
  showRequestNewDatasourceDialogAtom,
} from '@/atoms/dataSources/dataSourcesAtoms';
import { AddCustomDataSourceCard } from '@/components/dataSources/addCustomDataSourceCard/AddCustomDataSourceCard';
import { AddDataSourcesTable } from '@/components/dataSources/addDataSourcesTable/AddDataSourcesTable';
import DataSourceCardSkeleton from '@/components/dataSources/dataSourceCard/dataSourceCardSkeleton/DataSourceCardSkeleton';
import { DataSourceFilter } from '@/components/dataSources/dataSourceFilter/DataSourceFilter';
import { DataSourceViewModeToggle } from '@/components/dataSources/dataSourceViewModeToggle/DataSourceViewModeToggle';
import DatasourcePresetCard from '@/components/dataSources/presetCard/DatasourcePresetCard';
import { RequestNewDatasourceCard } from '@/components/dataSources/requestNewDatasourceCard/RequestNewDatasourceCard';
import { RequestNewDatasourceDialog } from '@/components/dialog/requestNewDatasource/RequestNewDatasourceDialog';
import { useGetPresetDataSources } from '@/hooks/content/useGetPresetDataSources';
import { usePresetPurgeCache } from '@/hooks/content/usePresetPurgeCache';
import { invalidatePresetCachesTooltip } from '@/strings/StringsTooltips';
import { capitalize } from '@/utils/capitalize';

interface AddDataSourceViewProps {
  dataSourcePresets: content_v1_PresetDataSourceList;
  isLoading: boolean;
}

const AddDataSourcesCardView = ({
  dataSourcePresets,
  isLoading,
}: AddDataSourceViewProps) => {
  const { enableCustomDatasources, enableRequestDatasource } =
    useFlags<FlagSet>();

  if (isLoading) {
    return (
      <div
        className="grid grid-cols-1 gap-4 sm:grid-cols-2 md:grid-cols-3"
        data-testid="add-datasources-loading"
      >
        {Array.from({ length: 9 }).map((_, index) => (
          <DataSourceCardSkeleton
            key={index}
            variant="preset"
            data-testid={`add-datasource-card-skeleton-${index}`}
          />
        ))}
      </div>
    );
  }

  return (
    <div
      className="m-auto flex h-fit w-full flex-col"
      data-testid="add-datasources-section"
    >
      <div
        className="grid grid-cols-1 gap-4 sm:grid-cols-2 md:grid-cols-3"
        data-testid="add-datasources-grid"
      >
        {enableCustomDatasources && (
          <AddCustomDataSourceCard data-testid="add-custom-datasource-card" />
        )}
        {dataSourcePresets.items.map((dataSourcePreset, index) => (
          <DatasourcePresetCard
            key={index}
            preset={dataSourcePreset}
            data-testid={`preset-datasource-card-${dataSourcePreset.name!}`}
          />
        ))}
        {enableRequestDatasource && (
          <RequestNewDatasourceCard data-testid="request-new-datasource-card" />
        )}
      </div>
    </div>
  );
};

const AddDataSourcesTableView = ({
  dataSourcePresets,
  isLoading,
}: AddDataSourceViewProps) => {
  return (
    <AddDataSourcesTable
      dataSourcePresets={dataSourcePresets}
      isLoading={isLoading}
    />
  );
};

const AddDataSourcesHeader = () => {
  const { mutateAsync, isPending } = usePresetPurgeCache(false);

  const handlePurgeCache = useCallback(async () => {
    await mutateAsync(undefined, {
      toasts: {
        success: 'Preset caches invalidated',
        error: (error) => {
          const errorData = error.response?.data as AxiosError<{
            message: string;
          }>;

          const errorMessage =
            capitalize(errorData?.message) ||
            'Failed to invalidate preset caches. Please try again.';

          return errorMessage;
        },
      },
    });
  }, [mutateAsync]);

  return (
    <div className="mb-2 flex flex-col gap-2">
      <div className="flex items-center gap-2">
        <Text
          size="lg"
          className="mb-4 mr-auto w-full text-xl font-bold"
          data-testid="current-datasources-title"
        >
          Add datasources
        </Text>
      </div>
      <div className="flex items-center justify-between gap-2">
        <DataSourceFilter />
        <div className="flex items-center gap-2">
          <Tooltip content={invalidatePresetCachesTooltip} side="top">
            <Button
              variant="default"
              onClick={handlePurgeCache}
              disabled={isPending}
              leftIcon="radix-icons:update"
              iconOnly
              animateIcon={isPending}
            />
          </Tooltip>
          <DataSourceViewModeToggle />
        </div>
      </div>
    </div>
  );
};

const AddDataSourcesContent = () => {
  const viewMode = useAtomValue(dataSourceViewModeAtom);
  const [fuzzyFilter, setFuzzyFilter] = useAtom(dataSourceFilterAtom);

  const {
    data: dataSourcePresets,
    isLoading,
    isFetching,
  } = useGetPresetDataSources();

  const filteredPresets = useGenericFuzzyFiltering(
    dataSourcePresets?.items || [],
    fuzzyFilter ?? '',
    [
      { name: 'name', getFn: (item) => item.name || '' },
      { name: 'description', getFn: (item) => item.description || '' },
      { name: 'title', getFn: (item) => item.title || '' },
      { name: 'source', getFn: (item) => item.source || '' },
      { name: 'sourceType', getFn: (item) => item.sourceType || '' },
    ],
  );

  if (!filteredPresets.length && !isLoading && !isFetching) {
    return (
      <div
        className="m-auto flex h-fit w-full flex-col"
        data-testid="current-datasources-section-empty"
      >
        <div className="flex flex-col items-center justify-center gap-2">
          <Icon
            icon="antimatterhq:custom-datasource"
            width={100}
            className="text-grey-400 dark:text-grey-400"
          />
          <Spacer size="base" />
          <div className="flex flex-col items-center gap-2">
            <Text size="lg" semiBold color="info" className="text-grey-400">
              No datasources found.
            </Text>
            <Spacer size="base" />
            <Button
              variant="primary"
              onClick={() => setFuzzyFilter('')}
              data-testid="add-datasource-button"
            >
              Clear search
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <>
      {viewMode === 'card' ? (
        <AddDataSourcesCardView
          dataSourcePresets={{ items: filteredPresets }}
          isLoading={isLoading || isFetching}
        />
      ) : (
        <AddDataSourcesTableView
          dataSourcePresets={{ items: filteredPresets }}
          isLoading={isLoading || isFetching}
        />
      )}
    </>
  );
};

export const AddDataSources = () => {
  const showRequestNewDatasourceDialog = useAtomValue(
    showRequestNewDatasourceDialogAtom,
  );

  const resetFuzzyFilter = useAtomCallback(
    useCallback((_get, set) => {
      set(dataSourceFilterAtom, '');
    }, []),
  );

  useEffect(() => {
    resetFuzzyFilter();
  }, [resetFuzzyFilter]);

  return (
    <>
      <AddDataSourcesHeader />
      <AddDataSourcesContent />
      {showRequestNewDatasourceDialog ? <RequestNewDatasourceDialog /> : null}
    </>
  );
};
